import { useFoundingStats } from "../hooks/useFoundingStats";
import { IMG } from "../data";

const scrollToWaitlist = () => {
  const el = document.getElementById("waitlist");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Founding50Section() {
  const { founding_remaining, founding_cap, total, founding_full, loading } = useFoundingStats();

  const filled = Math.min(founding_cap, total);
  const pct = Math.min(100, (filled / founding_cap) * 100);

  return (
    <section
      className="section relative overflow-hidden"
      style={{ background: "var(--brown)" }}
      data-testid="founding-50-section"
    >
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url(${IMG.mistyMountains})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      ></div>
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(120deg, rgba(61,43,31,0.95) 0%, rgba(61,43,31,0.7) 100%)" }}
      ></div>

      <div className="container-cu relative z-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p
            className="eyebrow eyebrow-light mb-5 inline-flex items-center gap-3"
            style={{ color: "var(--gold-soft)" }}
          >
            <span className="w-8 h-px" style={{ background: "var(--gold)" }}></span>
            An invitation
          </p>
          <h2
            className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-8"
            style={{ color: "var(--cream)" }}
          >
            The <span className="heading-italic" style={{ color: "var(--gold)" }}>Founding 50</span>
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mb-6 max-w-xl" style={{ color: "rgba(250,247,242,0.88)" }}>
            The first 50 guests on the waitlist become our{" "}
            <span style={{ color: "var(--gold-soft)" }}>Founding 50</span> — a small,
            quiet circle who shape how Casa Haya opens its doors.
          </p>
          <ul className="space-y-3 mb-8 text-sm sm:text-base" style={{ color: "rgba(250,247,242,0.85)" }}>
            {[
              "A personal welcome note, hand-written by Ramona",
              "First pick on opening week 2026 — before public availability",
              "A founding rate, honoured for every future stay",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3">
                <i
                  className="fa-solid fa-leaf mt-1.5 text-xs"
                  style={{ color: "var(--gold)" }}
                ></i>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={scrollToWaitlist}
            className="btn-cream"
            data-testid="founding-cta"
            disabled={founding_full}
            style={founding_full ? { opacity: 0.6, cursor: "not-allowed" } : {}}
          >
            {founding_full ? (
              <>Waitlist Full <i className="fa-solid fa-lock text-sm"></i></>
            ) : (
              <>Claim Your Spot <i className="fa-solid fa-arrow-right text-sm"></i></>
            )}
          </button>
        </div>

        {/* Counter card */}
        <div className="lg:col-span-5">
          <div
            className="rounded-md p-8 sm:p-10"
            style={{
              background: "rgba(250,247,242,0.05)",
              border: "1px solid rgba(201, 168, 76, 0.4)",
              backdropFilter: "blur(8px)",
            }}
          >
            <p
              className="text-xs uppercase tracking-[0.25em] mb-4"
              style={{ color: "var(--gold-soft)" }}
            >
              {loading ? "Loading…" : founding_full ? "Founding 50 · closed" : "Founding 50 · live count"}
            </p>
            <div className="flex items-baseline gap-2 mb-6">
              <span
                className="font-serif text-7xl sm:text-8xl tabular-nums leading-none"
                style={{ color: "var(--gold)" }}
                data-testid="founding-remaining-large"
              >
                {loading ? "—" : founding_remaining}
              </span>
              <span
                className="font-serif text-2xl heading-italic"
                style={{ color: "rgba(250,247,242,0.7)" }}
              >
                of 50
              </span>
            </div>
            <p
              className="text-sm mb-5"
              style={{ color: "rgba(250,247,242,0.78)" }}
            >
              {founding_full
                ? "All 50 founding seats are taken. You'll join the standard waitlist instead."
                : "spots left for the Founding 50."}
            </p>

            {/* Progress bar */}
            <div
              className="h-2 rounded-full overflow-hidden mb-3"
              style={{ background: "rgba(250,247,242,0.12)" }}
              data-testid="founding-progress"
            >
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${pct}%`,
                  background: "linear-gradient(90deg, var(--gold) 0%, var(--gold-soft) 100%)",
                }}
              ></div>
            </div>
            <div
              className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em]"
              style={{ color: "rgba(250,247,242,0.6)" }}
            >
              <span>{loading ? "—" : `${filled} claimed`}</span>
              <span>50 founding seats</span>
            </div>
          </div>

          <p
            className="mt-6 text-xs text-center italic"
            style={{ color: "rgba(250,247,242,0.55)" }}
          >
            Counter updates in real time as new founders register.
          </p>
        </div>
      </div>
    </section>
  );
}
